import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, Wine } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const SLOTS = ["18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00"];
const DRAFT_KEY = "sn_table_reservation_draft";

const readDraft = () => {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};

const CATEGORY_LABELS = {
  signature: "Signatures",
  rhum_arrange: "Rhums arrangés primés",
  mocktail: "Mocktails (sans alcool)",
  calvados: "Cocktails au Calvados",
  rhum: "Cocktails au Rhum",
  glace: "Cocktails glacés",
  vin_biere: "Vins & Bières",
  classique: "Classiques",
  sans_alcool: "Sans alcool",
};
const CATEGORY_ORDER = ["signature", "rhum_arrange", "mocktail", "calvados", "rhum", "glace", "vin_biere", "classique", "sans_alcool"];

export default function TableReservation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [requests, setRequests] = useState("");
  const [snacks, setSnacks] = useState([]);
  const [snackList, setSnackList] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [cocktailQty, setCocktailQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(null);

  // Restore a draft saved before the login redirect
  useEffect(() => {
    const d = readDraft();
    if (!d) return;
    if (d.date) setDate(d.date);
    if (d.slot) setSlot(d.slot);
    if (d.guests) setGuests(d.guests);
    if (d.name) setName(d.name);
    if (d.phone) setPhone(d.phone);
    if (d.email) setEmail(d.email);
    if (d.requests) setRequests(d.requests);
    if (Array.isArray(d.snacks)) setSnacks(d.snacks);
    if (d.cocktailQty) setCocktailQty(d.cocktailQty);
    setRestoredDraft(true);
  }, []);

  // Pre-fill from logged-in user
  useEffect(() => {
    if (user?.name && !name) setName(user.name);
    if (user?.phone && !phone) setPhone(user.phone);
    if (user?.email && !email) setEmail(user.email);
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/snacks").then(({ data }) => setSnackList(data)).catch(() => {});
    api.get("/cocktails").then(({ data }) => setCocktails(data)).catch(() => {});
    // Load static catalogs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Snacks need ≥24h notice — block selection when reservation date is too close
  const snackPreorderTooLate = useMemo(() => {
    if (!date || !slot) return false;
    try {
      const target = new Date(`${date}T${slot}:00`);
      return (target.getTime() - Date.now()) < 24 * 3600 * 1000;
    } catch {
      return false;
    }
  }, [date, slot]);

  const toggleSnack = (id) => {
    setSnacks((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const updateCocktailQty = (id, delta) => {
    setCocktailQty((prev) => {
      const next = Math.max(0, Math.min(20, (prev[id] || 0) + delta));
      const copy = { ...prev };
      if (next === 0) delete copy[id];
      else copy[id] = next;
      return copy;
    });
  };

  const cocktailPreorder = useMemo(() => {
    return Object.entries(cocktailQty)
      .map(([id, qty]) => {
        const c = cocktails.find((x) => x.id === id);
        if (!c || qty < 1) return null;
        return { id, name: c.name, quantity: qty, price: c.price, line_total: c.price * qty };
      })
      .filter(Boolean);
  }, [cocktailQty, cocktails]);

  const cocktailTotal = cocktailPreorder.reduce((s, i) => s + i.line_total, 0);
  const totalCocktails = cocktailPreorder.reduce((s, i) => s + i.quantity, 0);

  const cocktailsByCategory = useMemo(() => {
    const groups = {};
    for (const c of cocktails) {
      const cat = c.category || "signature";
      groups[cat] = groups[cat] || [];
      groups[cat].push(c);
    }
    return groups;
  }, [cocktails]);

  // Pre-filter CATEGORY_ORDER so the JSX doesn't re-filter on every render.
  const visibleCategories = useMemo(
    () => CATEGORY_ORDER.filter((cat) => cocktailsByCategory[cat]?.length),
    [cocktailsByCategory],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return toast.error("Date et créneau requis");
    if (!name.trim()) return toast.error("Votre nom est obligatoire");
    if (!phone.trim() || phone.replace(/\D/g, "").length < 6) return toast.error("Un numéro de téléphone valide est obligatoire");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("Une adresse email valide est obligatoire pour recevoir la confirmation");
    if (snacks.length > 0 && snackPreorderTooLate) return toast.error("Les planches & tapas nécessitent un préavis de 24h. Veuillez décocher votre sélection ou choisir une date plus tardive.");
    setSubmitting(true);
    try {
      const snackNames = snacks.map((id) => snackList.find((s) => s.id === id)?.name).filter(Boolean);
      await api.post("/table-reservations", {
        date, time_slot: slot, num_guests: parseInt(guests, 10),
        contact_name: name.trim(),
        contact_phone: phone.trim(),
        contact_email: email.trim(),
        snack_selection: snackNames,
        cocktail_preorder: cocktailPreorder.map(({ name, quantity, price, line_total }) => ({
          name, quantity, price, line_total,
        })),
        special_requests: requests,
      });
      sessionStorage.removeItem(DRAFT_KEY);
      toast.success("Demande de réservation envoyée ! Nous vous confirmons très vite.");
      if (user) nav("/compte");
      else { setDone({ date, slot, guests, email: email.trim(), name: name.trim() }); window.scrollTo({ top: 0, behavior: "smooth" }); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="table-reservation-page">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        {/* Colonne gauche — formulaire */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Au bar</div>
          <h1 className="font-display text-5xl lg:text-6xl mb-4">Réserver une table</h1>
          <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">
            Profitez de notre carte signature dans une ambiance feutrée. Sélectionnez votre créneau, le nombre de convives, ajoutez des planches et — pour gagner du temps à votre arrivée — pré-commandez vos cocktails à droite.
          </p>

          {restoredDraft && user && (
            <div className="rounded-xl border border-primary/60 bg-primary/10 p-4 mb-6 flex items-start gap-3" data-testid="table-draft-banner">
              <Wine className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <b className="text-foreground">Votre réservation n'est pas encore envoyée.</b> Nous avons conservé vos informations — vérifiez-les puis cliquez sur <b className="gold-text">« Confirmer la demande »</b> pour la transmettre au bar.
              </div>
            </div>
          )}
          {!user && !done && (
            <div className="rounded-xl border border-border bg-input/40 p-4 mb-6 text-sm text-muted-foreground" data-testid="table-login-notice">
              <b className="text-foreground">Pas besoin de compte</b> : nom, téléphone et email suffisent, la confirmation arrive par email.{" "}
              <button
                type="button"
                className="gold-text hover:underline"
                data-testid="table-login-link"
                onClick={() => {
                  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ date, slot, guests, name, phone, email, requests, snacks, cocktailQty }));
                  nav("/connexion?next=/reservation-table");
                }}
              >
                Connectez-vous
              </button>{" "}
              si vous souhaitez cumuler des points fidélité et retrouver vos réservations.
            </div>
          )}
          {done && (
            <div className="rounded-2xl border border-primary/60 bg-primary/10 p-6 lg:p-8 mb-8" data-testid="table-guest-success">
              <div className="smallcaps-label mb-3">Demande envoyée</div>
              <h2 className="font-display text-2xl md:text-3xl mb-3">Merci {done.name.split(" ")[0]}, c'est bien noté.</h2>
              <p className="text-sm leading-relaxed text-foreground/85">
                Table pour <b>{done.guests}</b> le <b>{done.date}</b> à <b>{done.slot}</b>. Un email de réception vient de partir à <b className="gold-text">{done.email}</b> ; Éric vous confirme très vite par email ou téléphone.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to={`/inscription?next=/compte`} className="btn-gold-marble inline-flex items-center gap-2 rounded-full" data-testid="table-guest-create-account">
                  Créer un compte pour cumuler des points
                </Link>
                <Link to="/carte" className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-6 h-11 text-sm transition-colors" data-testid="table-guest-see-menu">
                  Découvrir la carte
                </Link>
              </div>
            </div>
          )}
          <form onSubmit={submit} className={`space-y-6 rounded-xl border border-border bg-card p-6 lg:p-8 ${done ? "hidden" : ""}`} data-testid="table-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="table-date" className="bg-input mt-2" />
              </div>
              <div>
                <Label>Créneau</Label>
                <select
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  required
                  className="mt-2 flex h-10 w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="table-slot-trigger"
                >
                  <option value="" disabled>Choisir un créneau</option>
                  {SLOTS.map((s) => <option key={s} value={s} data-testid={`slot-${s}`}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="guests">Nombre de convives</Label>
              <Input id="guests" type="number" min={1} max={20} value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="table-guests" className="bg-input mt-2" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name">Nom complet <span className="text-primary">*</span></Label>
                <Input id="contact-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ex. Marie Dupont" data-testid="table-name" className="bg-input mt-2" />
              </div>
              <div>
                <Label htmlFor="contact-phone">Téléphone <span className="text-primary">*</span></Label>
                <Input id="contact-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="06 12 34 56 78" data-testid="table-phone" className="bg-input mt-2" />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-email">Email <span className="text-primary">*</span></Label>
              <Input id="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="vous@exemple.fr" data-testid="table-email" className="bg-input mt-2" />
              <p className="text-[11px] text-muted-foreground mt-1.5">Pour recevoir l'accusé de réception et la confirmation de votre table.</p>
            </div>

            {snackList.length > 0 && (
              <div>
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <Label className="inline-block">Planches & tapas (optionnel)</Label>
                  <span className="text-[11px] uppercase tracking-wider text-primary/80">Préavis 24h</span>
                </div>
                <div className="text-xs text-muted-foreground mb-3 italic" data-testid="snack-24h-notice">
                  Nos planches & tapas sont préparées avec des produits frais — merci de réserver au moins <strong className="text-foreground/80 not-italic">24 heures à l'avance</strong>.
                </div>
                {snackPreorderTooLate && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 mb-3 text-xs text-amber-200/90" data-testid="snack-24h-warning">
                    Le créneau choisi est dans moins de 24h — la sélection de planches est désactivée.
                  </div>
                )}
                <div className={`grid sm:grid-cols-2 gap-3 ${snackPreorderTooLate ? "opacity-50 pointer-events-none" : ""}`}>
                  {snackList.map((s) => (
                    <label key={s.id} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-input cursor-pointer hover:border-primary/50 transition-colors" data-testid={`snack-option-${s.id}`}>
                      <Checkbox checked={snacks.includes(s.id)} onCheckedChange={() => toggleSnack(s.id)} disabled={snackPreorderTooLate} />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{s.description}</div>
                        <div className="text-xs gold-text mt-1">{s.price.toFixed(2)} €</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Récap pré-commande cocktails (visible quand au moins 1 ajouté) */}
            {cocktailPreorder.length > 0 && (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-4" data-testid="table-preorder-recap">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium">Pré-commande cocktails ({totalCocktails})</div>
                  <div className="gold-text font-display text-lg">{cocktailTotal.toFixed(2)} €</div>
                </div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {cocktailPreorder.map((p) => (
                    <li key={p.id} className="flex justify-between">
                      <span>{p.quantity} × {p.name}</span>
                      <span>{p.line_total.toFixed(2)} €</span>
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] text-muted-foreground mt-3 italic">
                  Cocktails préparés à votre arrivée — réglés sur place.
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="req">Demandes spéciales</Label>
              <Textarea id="req" value={requests} onChange={(e) => setRequests(e.target.value)} placeholder="Anniversaire, allergies, table près de la fenêtre…" data-testid="table-requests" className="bg-input mt-2 min-h-[90px]" />
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="table-submit-btn">
              {submitting ? "Envoi…" : "Confirmer la demande"}
            </Button>
          </form>
        </div>

        {/* Colonne droite — carte cocktails interactive */}
        <aside className="rounded-xl border border-border bg-card p-6 lg:p-8 lg:sticky lg:top-28 max-h-[calc(100vh-8rem)] overflow-y-auto" data-testid="cocktail-preorder-panel">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Pré-commande</div>
          <h2 className="font-display text-2xl mb-2">Carte des cocktails</h2>
          <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
            Ajoutez vos cocktails à l'avance — ils seront prêts dès votre arrivée. Réglés sur place avec votre note.
          </p>

          {cocktails.length === 0 && (
            <div className="text-sm text-muted-foreground">Chargement de la carte…</div>
          )}

          {visibleCategories.map((cat) => (
            <div key={cat} className="mb-6 last:mb-0">
              <div className="text-[11px] uppercase tracking-[0.25em] text-primary/80 mb-3 pb-1 border-b border-border">
                {CATEGORY_LABELS[cat]}
              </div>
              <ul className="space-y-3">
                {cocktailsByCategory[cat].map((c) => {
                  const qty = cocktailQty[c.id] || 0;
                  return (
                    <li key={c.id} className="flex items-start gap-3" data-testid={`preorder-cocktail-${c.id}`}>
                      {c.image ? (
                        <img
                          src={c.image}
                          alt={c.name}
                          loading="lazy"
                          className="w-14 h-14 rounded-lg object-cover border border-border shrink-0"
                          data-testid={`preorder-thumb-${c.id}`}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg shrink-0 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center text-primary font-display text-lg">
                          {c.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-baseline gap-2">
                          <span className="truncate">{c.name}</span>
                          <span className="gold-text whitespace-nowrap text-xs">{c.price.toFixed(2)} €</span>
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{c.description}</div>
                      </div>
                      {qty === 0 ? (
                        <button
                          type="button"
                          onClick={() => updateCocktailQty(c.id, +1)}
                          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                          data-testid={`preorder-add-${c.id}`}
                          aria-label={`Ajouter ${c.name}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="shrink-0 inline-flex items-center gap-2 rounded-full border border-primary/60 h-9 px-2">
                          <button type="button" onClick={() => updateCocktailQty(c.id, -1)} className="w-6 h-6 inline-flex items-center justify-center hover:bg-primary/10 rounded-full" data-testid={`preorder-dec-${c.id}`}>
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-sm font-semibold w-4 text-center" data-testid={`preorder-qty-${c.id}`}>{qty}</span>
                          <button type="button" onClick={() => updateCocktailQty(c.id, +1)} className="w-6 h-6 inline-flex items-center justify-center hover:bg-primary/10 rounded-full" data-testid={`preorder-inc-${c.id}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {cocktailPreorder.length > 0 && (
            <div className="border-t border-primary/30 pt-4 mt-2 flex items-center justify-between" data-testid="preorder-total-bottom">
              <div className="flex items-center gap-2 text-sm">
                <Wine className="w-4 h-4 text-primary" />
                <span>{totalCocktails} cocktail{totalCocktails > 1 ? "s" : ""} pré-commandé{totalCocktails > 1 ? "s" : ""}</span>
              </div>
              <div className="gold-text font-display text-xl">{cocktailTotal.toFixed(2)} €</div>
            </div>
          )}

          <Button asChild variant="outline" className="w-full mt-6 rounded-full" data-testid="see-full-menu-btn">
            <a href="/carte">Voir la carte complète</a>
          </Button>
        </aside>
      </div>
    </div>
  );
}
